import React, { useCallback } from 'react';
import { Box, Stack, Switch, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { Info } from 'components';
import { IApiRequests, useApiRequests, useSignalrConnection } from 'hooks';
import { ApiClinicUser, ApiUser } from 'types';
import { useAppDispatch, useAppSelector } from 'store';
import { getUser, selectUser } from 'store/user';
import styles from './NotificationSettings.module.scss';

export function NotificationSettings() {
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';

  const dispatch = useAppDispatch();
  const user: ApiUser = useAppSelector(selectUser)!;
  const { updateClinicUser }: IApiRequests = useApiRequests();
  const { updateUnavailableProviderStatus } = useSignalrConnection();

  const onChange = useCallback(
    (data: Partial<ApiClinicUser>) => {
      updateClinicUser({ ...data })
        .then(() => {
          if (Reflect.has(data, 'unavailable')) {
            updateUnavailableProviderStatus(data.unavailable ?? false);
          }

          return dispatch(getUser()).unwrap();
        })
        .catch((e) => enqueueSnackbar(t('updateClinicUserError', { error: e }), { variant: 'error' }));
    },
    [dispatch, enqueueSnackbar, t, updateClinicUser, updateUnavailableProviderStatus],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      [event.target.name]: event.target.checked,
    });
  };

  const handleChangeRevers = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      [event.target.name]: event.target.checked,
    });
  };

  return (
    <Box className={styles.container}>
      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' sx={{ columnGap: '0.4rem' }}>
          <Typography component='p' variant='body5' alignItems='center'>
            {t('receiveWaitingRoomMessages')}
          </Typography>
          <Box sx={{ display: 'inline-flex' }}>
            <Info text={t('receiveWaitingRoomMessagesInfo')} tooltipPlacement={isRtl ? 'left' : 'right'} />
          </Box>
        </Stack>
        <Switch
          checked={user.clinicUser.receiveMessages}
          name='receiveMessages'
          inputProps={{ 'aria-label': 'ReceiveMessages' }}
          onChange={handleChange}
        />
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' sx={{ columnGap: '0.4rem' }}>
          <Typography component='p' variant='body5'>
            {t('doNotDisturbWhileInMeetings')}
          </Typography>
          <Box sx={{ display: 'inline-flex' }}>
            <Info text={t('doNotDisturbWhileInMeetingsInfo')} tooltipPlacement={isRtl ? 'left' : 'right'} />
          </Box>
        </Stack>
        <Switch
          checked={!user.clinicUser.receiveMessagesInMeeting}
          name='receiveMessagesInMeeting'
          inputProps={{ 'aria-label': 'ReceiveMessagesInMeeting' }}
          onChange={handleChangeRevers}
        />
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' sx={{ columnGap: '0.4rem' }}>
          <Typography component='p' variant='body5'>
            {t('notifications')}
          </Typography>
          <Box sx={{ display: 'inline-flex' }}>
            <Info text={t('notificationsInfo')} tooltipPlacement={isRtl ? 'left' : 'right'} />
          </Box>
        </Stack>
        <Switch
          checked={user.clinicUser.notifications}
          name='notifications'
          inputProps={{ 'aria-label': 'Notifications' }}
          onChange={handleChange}
        />
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' sx={{ columnGap: '0.4rem' }}>
          <Typography component='p' variant='body5'>
            {t('unavailable')}
          </Typography>
          <Box sx={{ display: 'inline-flex' }}>
            <Info text={t('unavailableInfo')} tooltipPlacement={isRtl ? 'left' : 'right'} />
          </Box>
        </Stack>
        <Switch
          checked={user.clinicUser.unavailable}
          name='unavailable'
          inputProps={{ 'aria-label': 'Unavailable' }}
          onChange={handleChange}
        />
      </Stack>
    </Box>
  );
}

export default React.memo(NotificationSettings);
